"use client";

import { useQuery } from "@tanstack/react-query";

import { allQueryKeys } from "#app/_api/allQueryKeys";
import { PendingCardContent } from "#app/_ui/components/cards/PendingCard";
import { Tel } from "#app/_ui/components/media/Tel";
import { hoursCashManagement, telCashManagement } from "#app/lib/company";
import { logError } from "#app/lib/logger";
import { toUsaTelHref } from "#app/lib/telephone";
import { Alert, AlertContent, AlertTitle } from "#ui/alert";
import { Card, CardContent } from "#ui/card";
import {
  CardTabs,
  CardTabsContent,
  CardTabsList,
  CardTabsTrigger,
} from "#ui/card-tabs";

import { CheckImage } from "./check-image";
import { CheckImageDialog } from "./check-image-dialog";
import { CheckImageUnavailable } from "./check-image-unavailable";

export function CheckImageViewer({ checkId }: { checkId: string }) {
  const { data, error, isError, isPending } = useQuery(
    allQueryKeys.externalFundTransferService.checkImages({
      compositeId: checkId,
      convertTo: "PNG",
    }),
  );

  if (isPending) {
    return <PendingCardContent />;
  }

  const hasImageFront = !!data?.front && data.front.length > 0;
  const hasImageBack = !!data?.back && data.back.length > 0;
  const hasAtLeastOneImage = hasImageFront || hasImageBack;

  if (isError || hasAtLeastOneImage === false) {
    logError(error);
    return <CheckImageUnavailable />;
  }

  return (
    <div className="grid gap-20px sm:gap-30px">
      <CardTabs defaultValue={hasImageFront ? "front" : "back"}>
        <CardTabsList
          placement="top-outside"
          className="justify-center *:basis-100px"
        >
          <CardTabsTrigger value="front" disabled={!hasImageFront}>
            Front
          </CardTabsTrigger>
          <CardTabsTrigger value="back" disabled={!hasImageBack}>
            Back
          </CardTabsTrigger>
        </CardTabsList>
        <Card variant="white-border" className="bg-shade-2">
          <CardContent>
            <CardTabsContent value="front">
              {hasImageFront ? (
                <CheckImageDialog>
                  <CheckImage value={data.front} alt="front of the check" />
                </CheckImageDialog>
              ) : null}
            </CardTabsContent>
            <CardTabsContent value="back">
              {hasImageBack ? (
                <CheckImageDialog>
                  <CheckImage value={data.back} alt="back of the check" />
                </CheckImageDialog>
              ) : null}
            </CardTabsContent>
          </CardContent>
        </Card>
      </CardTabs>

      <Alert intent="help">
        <AlertContent>
          <AlertTitle>
            If you have any questions regarding the check image above, please
            contact{" "}
            <Tel href={telCashManagement} hrefFn={toUsaTelHref}>
              {telCashManagement}
            </Tel>
            , {hoursCashManagement}.
          </AlertTitle>
        </AlertContent>
      </Alert>
    </div>
  );
}
